// src/components/charts/pivotViewerGrain.ts
//
// A dashboard viewer's own period grain for a pivot over time (plan
// 2026-10-06 pivot period grain, D3). The author opts a tile in with
// `pivot.columns.viewer_choice`; the viewer's choice is remembered per
// browser and per tile in localStorage, the same model as the column choice
// (`useStoredColumnChoice`, ADR-0206). No backend state.
//
// Two readers share this module and must never disagree on what is active:
// the tile's Period button (PivotTableRenderer) and the page's refresh flow
// (Insights.tsx), which leaves a tile with an active viewer grain out of the
// bulk call and out of the browser cache. A write dispatches a window event,
// so the page starts that tile's single refresh and every mounted button
// re-reads.
//
// The allowed list is the server's (`chart_spec_json.pivot_grain.allowed`,
// read-time only): nothing here decides which grains a tile can draw.

import { useCallback, useSyncExternalStore } from "react";

export const PIVOT_GRAINS = ["daily", "weekly", "monthly", "quarterly", "yearly"] as const;
export type PivotGrain = (typeof PIVOT_GRAINS)[number];

export const PIVOT_GRAIN_LABELS: Record<PivotGrain, string> = {
  daily: "Day",
  weekly: "Week",
  monthly: "Month",
  quarterly: "Quarter",
  yearly: "Year",
};

/** Namespace of the stored choice; the item's public id completes the key. */
export const PIVOT_GRAIN_STORAGE_PREFIX = "verity.pivot.grain.";

/** Dispatched on `window` after every write; `detail.itemId` names the tile. */
export const PIVOT_GRAIN_EVENT = "verity:pivot-grain-change";

export type PivotGrainChangeDetail = { itemId: string };

/** The refresh response's `chart_spec_json.pivot_grain`. */
export type PivotGrainInfo = {
  effective: PivotGrain | null;
  default: PivotGrain | null;
  allowed: PivotGrain[];
  block: PivotGrain | null;
  viewer_choice: boolean;
};

function isGrain(value: unknown): value is PivotGrain {
  return typeof value === "string" && (PIVOT_GRAINS as readonly string[]).includes(value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** The server's resolution on a refreshed spec, or null when the spec has
 *  none (not refreshed yet, or hydrated from the dashboard GET, which does
 *  not carry it). */
export function readPivotGrainInfo(spec: unknown): PivotGrainInfo | null {
  const info = asRecord(asRecord(spec)?.pivot_grain);
  if (!info) return null;
  return {
    effective: isGrain(info.effective) ? info.effective : null,
    default: isGrain(info.default) ? info.default : null,
    allowed: Array.isArray(info.allowed) ? info.allowed.filter(isGrain) : [],
    block: isGrain(info.block) ? info.block : null,
    viewer_choice: info.viewer_choice === true,
  };
}

/** The author's opt-in, read from the stored spec: a pivot over time with
 *  `pivot.columns.viewer_choice: true`. */
export function pivotViewerChoiceOn(spec: unknown): boolean {
  const s = asRecord(spec);
  if (!s || s.chart_type !== "pivot_table") return false;
  const columns = asRecord(asRecord(s.pivot)?.columns);
  return columns?.kind === "time" && columns.viewer_choice === true;
}

/** The raw stored choice, or null. Never throws: blocked storage reads as
 *  "no choice". */
export function readStoredPivotGrain(itemId: string | null | undefined): PivotGrain | null {
  if (!itemId) return null;
  try {
    const raw = localStorage.getItem(`${PIVOT_GRAIN_STORAGE_PREFIX}${itemId}`);
    return isGrain(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Store (or, with null, clear) the choice and tell the page. The event is
 *  dispatched even when storage throws, so the tile still refreshes; the
 *  choice then holds for nothing beyond that request. */
export function writeStoredPivotGrain(itemId: string, grain: PivotGrain | null): void {
  try {
    const key = `${PIVOT_GRAIN_STORAGE_PREFIX}${itemId}`;
    if (grain === null) localStorage.removeItem(key);
    else localStorage.setItem(key, grain);
  } catch {
    // Storage unavailable: nothing is remembered.
  }
  window.dispatchEvent(
    new CustomEvent<PivotGrainChangeDetail>(PIVOT_GRAIN_EVENT, { detail: { itemId } }),
  );
}

/**
 * The grain this browser asks for on a tile, or null for the tile's default.
 *
 * Active only while the author's `viewer_choice` is on and the stored grain
 * is in the server's `allowed` list. A choice that fails either test is
 * ignored but left in storage, for when the author switches it on again.
 * Without a `pivot_grain` on the spec (the GET does not carry one) any of the
 * five grains counts: the server applies the same rule (rule 1) and answers
 * with the default when the grain is not allowed, and that answer carries the
 * list from then on.
 */
export function activeViewerGrain(item: { id?: string; chart_spec_json?: unknown }): PivotGrain | null {
  if (!pivotViewerChoiceOn(item.chart_spec_json)) return null;
  const stored = readStoredPivotGrain(item.id);
  if (!stored) return null;
  const info = readPivotGrainInfo(item.chart_spec_json);
  if (info && !info.allowed.includes(stored)) return null;
  return stored;
}

// Only this page's own writes: a choice made in another browser tab is not
// followed here (no `storage` listener), because this page's refresh flow
// would not follow it either and the button would name a grain the tile does
// not draw. A reload picks it up.
function subscribe(onChange: () => void): () => void {
  window.addEventListener(PIVOT_GRAIN_EVENT, onChange);
  return () => window.removeEventListener(PIVOT_GRAIN_EVENT, onChange);
}

/** The stored choice for one tile, re-read on every write. */
export function useStoredPivotGrain(itemId: string | null): PivotGrain | null {
  const getSnapshot = useCallback(() => readStoredPivotGrain(itemId), [itemId]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** A tile's item after a write, with the pivot result it already shows kept
 *  while it is drawn at the viewer's own period: a write answers with the
 *  tile's DEFAULT-period result, and merging that would switch the tile back
 *  to the default period under a viewer who chose another (found in the
 *  browser check of the period-label plan, 2026-10-06). The labels are made
 *  at draw time from the merged spec's style, so a style write still shows. */
export function keepViewerPivotResult<T extends { chart_spec_json?: unknown }>(
  shown: T,
  merged: T,
  viewerGrain: string | null,
): T {
  if (!viewerGrain) return merged;
  const shownSpec = asRecord(shown.chart_spec_json);
  if (!shownSpec || !("pivot_result" in shownSpec)) return merged;
  return {
    ...merged,
    chart_spec_json: {
      ...(asRecord(merged.chart_spec_json) ?? {}),
      pivot_result: shownSpec.pivot_result,
      ...("pivot_grain" in shownSpec ? { pivot_grain: shownSpec.pivot_grain } : {}),
    },
  };
}
